import { Alert, Button, Flex, Segmented, Table, Tag, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes"
import { isArchivingStatus } from "../../Projects/constants/project-statuses.ts"
import * as services from "../api/services.ts"
import type { SubcontractorProjectRow } from "../types/types.ts"

const { Text } = Typography

interface SubcontractorProjectsTableProps {
  subcontractorId: string
  /** The compact form used on Overview: a few rows and a link to the full tab. */
  preview?: boolean
  onViewAll?: () => void
}

export const SubcontractorProjectsTable = ({
  subcontractorId,
  preview = false,
  onViewAll
}: SubcontractorProjectsTableProps) => {
  const { t } = useTranslation()
  const [view, setView] = useState<"active" | "completed">("active")
  const [rows, setRows] = useState<SubcontractorProjectRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    const response = await services.getSubcontractorProjects(subcontractorId, {
      page_size: preview ? 5 : 50
    })
    if (response.ok && response.data) {
      const payload = response.data as unknown as {
        results?: SubcontractorProjectRow[]
        count?: number
      }
      const results = Array.isArray(payload.results)
        ? payload.results
        : (response.data as unknown as SubcontractorProjectRow[])
      setRows(Array.isArray(results) ? results : [])
      setTotal(payload.count ?? (Array.isArray(results) ? results.length : 0))
    } else {
      // A failed load must not read as "this subcontractor is on no projects":
      // an empty table and a broken one look identical otherwise.
      setFailed(true)
      setRows([])
    }
    setLoading(false)
  }, [subcontractorId, preview])

  useEffect(() => {
    void load()
  }, [load])

  const visible = rows.filter((row) =>
    // "Completed" here means the project is finished with, which the app
    // already defines as its archiving statuses (Completed and Cancelled).
    // Reused rather than restated — writing the numbers out is how you end up
    // asserting COMPLETED is 4 when 4 is On Hold.
    view === "completed"
      ? isArchivingStatus(row.project_status)
      : !isArchivingStatus(row.project_status)
  )

  if (failed) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("subcontractorDetail.projectsFailed")}
        action={
          <Button size="small" onClick={() => void load()}>
            {t("subcontractorDetail.retry")}
          </Button>
        }
      />
    )
  }

  const columns = [
    {
      title: t("subcontractorDetail.projectsColumns.project"),
      dataIndex: "name",
      render: (name: string | null, row: SubcontractorProjectRow) =>
        row.project_id ? (
          <Link to={RoutesPaths.project.view(row.project_id).overview}>
            {name || t("subcontractorDetail.untitledProject")}
          </Link>
        ) : (
          name || "—"
        )
    },
    {
      title: t("subcontractorDetail.projectsColumns.assignedWork"),
      dataIndex: "assigned_work",
      render: (work: string | null) =>
        work || <Text type="secondary">—</Text>
    },
    {
      title: t("subcontractorDetail.projectsColumns.location"),
      dataIndex: "city",
      render: (_: unknown, row: SubcontractorProjectRow) =>
        [row.city, row.state].filter(Boolean).join(", ") || "—"
    },
    {
      title: t("subcontractorDetail.projectsColumns.timeline"),
      dataIndex: "start_date",
      render: (_: unknown, row: SubcontractorProjectRow) =>
        row.start_date || row.end_date
          ? `${row.start_date ?? "—"} – ${row.end_date ?? "—"}`
          : "—"
    },
    {
      title: t("subcontractorDetail.projectsColumns.status"),
      dataIndex: "project_status_display",
      render: (status: string | null) =>
        status ? <Tag>{status}</Tag> : <Text type="secondary">—</Text>
    }
  ]

  return (
    <Flex vertical gap={12}>
      {!preview && (
        <Flex justify="flex-end">
          <Segmented
            size="small"
            value={view}
            onChange={(value) => setView(value as "active" | "completed")}
            options={[
              {
                label: t("subcontractorDetail.active"),
                value: "active"
              },
              {
                label: t("subcontractorDetail.completed"),
                value: "completed"
              }
            ]}
          />
        </Flex>
      )}

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={preview ? rows.slice(0, 5) : visible}
        pagination={false}
        // Five columns do not fit a phone; the table scrolls inside its own
        // card rather than pushing the whole page sideways.
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t("subcontractorDetail.noProjects") }}
      />

      {preview && total > rows.length && onViewAll && (
        <Flex justify="flex-end">
          <Button type="link" onClick={onViewAll}>
            {t("subcontractorDetail.viewAllProjects", { count: total })}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}
